/**
 * OnboardingCarousel — 4-screen first-run experience (PRD §3 #9).
 *
 * Slides are loaded from `ONBOARDING_SLIDES` in
 * `lib/effectiveslp/onboardingCopy.ts`. The HIPAA + 1.4.1 + 5.1.2(i)
 * language is verbatim from the PRD and pinned by the unit test in
 * `tests/lib/onboardingCopy.test.ts`.
 *
 * Once the user finishes the carousel:
 *   * localStorage flag → flipped (pre-sign-in first-run only)
 *   * if they granted the 3rd-party AI consent, we persist
 *     `ai_consent_granted_at` on the server once signed in
 *   * `onboarding_completed_at` is set on the server once signed in
 *
 * `App.tsx` calls `onDone()` and routes to `/auth` (or directly to
 * `/caseload` if already signed in).
 *
 * #430 — Slides 2 ("IMPORTANT — PLEASE READ") and 4 ("HIPAA + YOUR
 * DATA") require the user to scroll the body to the bottom before
 * the primary CTA enables. The "Skip" link is removed entirely (AC
 * #1 — option a). Slide 3 (AI consent) keeps the #401 checkbox gate.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../lib/supabase/client';
import { ONBOARDING_SLIDES } from '../lib/effectiveslp/onboardingCopy';
import { markOnboardingSeen } from '../lib/auth/onboardingStore';
import { BrandMonogram } from '../lib/effectiveslp/BrandMonogram';
import type { OnboardingSlide } from '../lib/effectiveslp/onboardingCopy';

interface Props {
  onDone: () => void;
  onAiConsentChange?: (granted: boolean) => void;
}

/**
 * Pixel tolerance for the "scrolled to bottom" check. Retina vs
 * non-Retina + sub-pixel layout can leave `scrollHeight -
 * clientHeight` a few pixels off zero even when the user is fully
 * scrolled; 8px is comfortable for both viewports while still
 * catching a meaningful "scrolled most of the way" gap.
 */
const SCROLL_BOTTOM_TOLERANCE_PX = 8;

/**
 * Per-slide CTA labels for the gated slides (#430). When the user has
 * scrolled to the bottom of the body, the label flips from the
 * scrolling-required copy to the explicit acknowledgement copy.
 */
const SCROLL_GATE_PENDING_LABEL: Record<'apple-141' | 'hipaa', string> = {
  'apple-141': 'Scroll to read',
  hipaa: 'Scroll to read',
};
const SCROLL_GATE_ACKNOWLEDGED_LABEL: Record<'apple-141' | 'hipaa', string> = {
  'apple-141': "I've read this — continue",
  hipaa: 'Acknowledge and continue',
};

export function OnboardingCarousel({
  onDone,
  onAiConsentChange,
}: Props): ReactElement {
  const [index, setIndex] = useState(0);
  const [aiConsent, setAiConsent] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollBodyRef = useRef<HTMLDivElement | null>(null);
  const slide: OnboardingSlide = ONBOARDING_SLIDES[index] ?? ONBOARDING_SLIDES[0]!;
  const isLast = index === ONBOARDING_SLIDES.length - 1;

  // Reset the scroll gate whenever the slide changes (so a user
  // returning to slide 2 from slide 3 cannot keep the previously-
  // scrolled acknowledgement).
  useEffect(() => {
    setScrolledToBottom(false);
  }, [index]);

  // fix(#430) + #454: after the slide change commits, measure the
  // scrollable body. If the content fits without overflowing (short
  // copy + large viewport), `onScroll` never fires and the gate is
  // unreachable — auto-satisfy so the user is not deadlocked. The
  // risk listed in the issue body (Risk #1) is mitigated by the
  // `max-height: 60vh` cap on `.onboarding-body--scrollable`, but
  // that cap does not *guarantee* overflow, so this measurement is
  // the safety net.
  useEffect(() => {
    const el = scrollBodyRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.clientHeight;
    if (remaining <= SCROLL_BOTTOM_TOLERANCE_PX) {
      setScrolledToBottom(true);
    }
  }, [index]);

  async function persistOnboardingCompletion(
    consentGranted: boolean,
  ): Promise<void> {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) return;
      const now = new Date().toISOString();
      await supabase
        .from('users')
        .update({
          onboarding_completed_at: now,
          ai_consent_granted_at: consentGranted ? now : null,
        })
        .eq('id', sessionData.session.user.id);
    } catch {
      // Best-effort — local flag still gates the first-run UI.
    }
  }

  async function finish(): Promise<void> {
    markOnboardingSeen();
    await persistOnboardingCompletion(aiConsent);
    onDone();
  }

  function handleScrollBody(): void {
    const el = scrollBodyRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.clientHeight - el.scrollTop;
    if (remaining <= SCROLL_BOTTOM_TOLERANCE_PX) {
      setScrolledToBottom(true);
    }
  }

  function next(): void {
    // fix(#401): on the third-party AI consent slide, the user must
    // explicitly check the consent box before advancing. The PRD intent
    // is that AI features are opt-in (SHIP-NOTES §5.1.2(i)) and the
    // persisted `users.ai_consent_granted_at` must match the user's
    // actual choice. Without this gate, the user could advance to slide
    // 4 with consent unchecked and `ai_consent_granted_at` would be
    // persisted as `null` by accident — leaving the user unsure whether
    // AI features were gated by consent.
    if (slide.requiresAiConsentToggle === true && !aiConsent) {
      return;
    }
    // fix(#430): slides 2 ("IMPORTANT — PLEASE READ") and 4 ("HIPAA +
    // YOUR DATA") require the user to scroll the body to the bottom
    // before the CTA enables. Without this gate the user could blow
    // through both disclaimers with a single tap and never read either
    // — a credibility problem for district privacy-officer review and
    // for Apple App Review (required disclaimer posture). Routed
    // through the same `isScrollGateSlide` predicate as `canAdvance`
    // below so the imperative guard and the `disabled` prop cannot
    // diverge.
    if (isScrollGateSlide && !scrolledToBottom) {
      return;
    }
    if (isLast) {
      void finish();
      return;
    }
    setIndex((i) => i + 1);
  }

  function handleAiConsentToggle(next: boolean): void {
    setAiConsent(next);
    onAiConsentChange?.(next);
  }

  // fix(#401): see next() — same gate applies to the "Skip" link so
  // the user cannot skip past the AI consent slide without explicitly
  // granting consent.
  const canAdvanceFromAiConsentSlide =
    slide.requiresAiConsentToggle !== true || aiConsent;
  const isScrollGateSlide =
    slide.key === 'apple-141' || slide.key === 'hipaa';
  // fix(#430): scope the scroll-bottom gate to the gated slides only.
  // The previous form `canAdvanceFromAiConsentSlide && scrolledToBottom`
  // applied the scroll gate to ALL slides — which deadlocked slide 1
  // (welcome, no scrollable body, no ref to satisfy it) and re-gated
  // slide 3 after the #401 checkbox (violating AC #4). The `isScrollGateSlide`
  // predicate is the same one `next()` uses, so the imperative guard and
  // the `disabled` prop now share one source of truth.
  const canAdvance =
    canAdvanceFromAiConsentSlide &&
    (!isScrollGateSlide || scrolledToBottom);

  let continueLabel: string;
  if (!canAdvanceFromAiConsentSlide) {
    continueLabel = 'Check the box to continue';
  } else if (slide.key === 'apple-141') {
    continueLabel = scrolledToBottom
      ? SCROLL_GATE_ACKNOWLEDGED_LABEL['apple-141']
      : SCROLL_GATE_PENDING_LABEL['apple-141'];
  } else if (slide.key === 'hipaa') {
    continueLabel = scrolledToBottom
      ? SCROLL_GATE_ACKNOWLEDGED_LABEL.hipaa
      : SCROLL_GATE_PENDING_LABEL.hipaa;
  } else {
    continueLabel = slide.primaryCta;
  }

  return (
    <main className="screen onboarding">
      <header className="screen-header">
        {index === 0 ? (
          <div className="auth-hero">
            <BrandMonogram size={96} breathing />
            <h1 className="onboarding-eyebrow">{slide.eyebrow}</h1>
          </div>
        ) : (
          <h1 className="onboarding-eyebrow">{slide.eyebrow}</h1>
        )}
      </header>

      <section className="onboarding-slide" aria-live="polite">
        <h2 className="onboarding-title">{slide.title}</h2>
        {isScrollGateSlide ? (
          // #430 — scrollable container so the user always has
          // something to scroll on Retina iPad screens where the body
          // might otherwise fit without scrolling. Mirrors the
          // existing max-height pattern on the long-copy screens
          // (e.g. InitialEvalForm at index.css ~L445).
          <div
            ref={scrollBodyRef}
            className="onboarding-body onboarding-body--scrollable"
            onScroll={handleScrollBody}
            tabIndex={0}
            aria-label="Slide content — scroll to read"
          >
            <p>{slide.body}</p>
            {/* #430 — ensure the scrollable body always overflows so the
                scroll-to-bottom gate is reachable even when the
                disclaimer copy is short and the viewport is large. */}
            <div className="onboarding-body-spacer" aria-hidden="true" />
          </div>
        ) : (
          <div className="onboarding-body">
            <p>{slide.body}</p>
          </div>
        )}
        {slide.requiresAiConsentToggle === true && (
          <label className="onboarding-toggle-row">
            <span>
              I consent to sending my goal text, child age, and materials
              list to our AI provider for AI-generated content.
            </span>
            <span className="onboarding-toggle">
              <input
                type="checkbox"
                checked={aiConsent}
                onChange={(e) => handleAiConsentToggle(e.target.checked)}
              />
              {aiConsent
                ? 'I consent to AI features'
                : 'I do NOT consent to AI features'}
            </span>
          </label>
        )}
      </section>

      <nav className="onboarding-dots" aria-label="Onboarding progress">
        {ONBOARDING_SLIDES.map((s, i) => (
          <span
            key={s.key}
            className={`onboarding-dot${i === index ? ' is-active' : ''}`}
            aria-current={i === index ? 'step' : undefined}
          />
        ))}
      </nav>

      <section className="onboarding-actions">
        <button
          type="button"
          className="snap-button"
          onClick={() => void next()}
          disabled={!canAdvance}
        >
          {continueLabel}
        </button>
      </section>
    </main>
  );
}
